import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { motion } from 'framer-motion'
import { CheckCircle, ArrowRight, Loader2 } from 'lucide-react'
import textBanner from '../assets/images/text-banner-black-min.png'

const VerificationSuccess = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user, loading } = useAuth()
  const [isRedirecting, setIsRedirecting] = useState(false)

  // Check if this is a verification redirect (has token and type params)
  const isVerificationRedirect = searchParams.get('token') && searchParams.get('type') === 'signup'
  
  useEffect(() => {
    // If user is already authenticated after verification, redirect to dashboard
    if (!loading && user) {
      setIsRedirecting(true)
      setTimeout(() => {
        navigate('/dashboard', { replace: true })
      }, 3000) // Give users time to see the success message
    }
  }, [user, loading, navigate])

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md mx-auto">
        <div className="relative">
          <div className="absolute -inset-1 bg-gradient-to-r from-green-400/30 via-emerald-400/30 to-teal-400/30 rounded-3xl blur-2xl -z-10" />
          <div className="glass-effect bg-white rounded-2xl shadow-soft p-6 sm:p-8">
            {/* Brand Banner */}
            <div className="text-center mb-6">
              <Link to="/" className="inline-flex items-center hover:opacity-80 transition-opacity">
                <div className="relative">
                  <img 
                    src={textBanner} 
                    alt="EasyAppIcon API" 
                    className="h-8"
                  />
                  <span className="absolute -top-1 -right-8 bg-[#df6e57] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                    API
                  </span>
                </div>
              </Link>
            </div>

            {/* Success Animation */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ 
                type: "spring",
                stiffness: 200,
                damping: 15,
                delay: 0.2 
              }}
              className="text-center mb-6"
            >
              <div className="mx-auto w-20 h-20 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-10 w-10 text-white" />
              </div>
            </motion.div>

            {/* Main Content */}
            <div className="text-center mb-8">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-slate-700 mb-3">
                Email Verified Successfully!
              </h1>
              
              {user ? (
                <div>
                  <p className="text-gray-600 mb-4">
                    Welcome to EasyAppIcon API! Your account is now active.
                  </p>
                  {isRedirecting && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                      <div className="flex items-center justify-center space-x-2 text-blue-700">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm">Redirecting to your dashboard...</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-gray-600 mb-4">
                    Your email has been verified successfully. You can now sign in to your account.
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-blue-800 text-left">
                        <p className="font-medium mb-1">Next steps:</p>
                        <ol className="list-decimal list-inside space-y-1 text-blue-700">
                          <li>Click "Sign In" below</li>
                          <li>Enter your email and password</li>
                          <li>Start generating app icons via API</li>
                        </ol>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="space-y-4">
              {user ? (
                <Link
                  to="/dashboard"
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-4 px-4 rounded-xl font-semibold focus:ring-2 focus:ring-green-500/20 focus:ring-offset-2 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:translate-y-0 inline-block text-center group"
                >
                  <div className="flex items-center justify-center space-x-2">
                    <span>Go to Dashboard</span>
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ) : (
                <Link
                  to="/auth"
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-4 px-4 rounded-xl font-semibold focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-2 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:translate-y-0 inline-block text-center group"
                >
                  <div className="flex items-center justify-center space-x-2">
                    <span>Sign In to Your Account</span>
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              )}

              <Link
                to="/"
                className="w-full border-2 border-gray-200 text-gray-600 hover:bg-gray-50 py-3 px-4 rounded-xl font-semibold transition-all duration-200 hover:border-gray-300 inline-block text-center"
              >
                Back to Home
              </Link>
            </div>

            {/* Help Text */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                Need help? Contact our support team if you experience any issues.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VerificationSuccess
